<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { CheckCircle2, CircleHelp, XCircle, Clock, UserPlus, Loader2, ChevronDown, ChevronRight, Copy, Check, ExternalLink, FileText } from 'lucide-vue-next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { API, HOST } from '@/utils/http'
import type { BridgeCustomer } from '@/types/bridge'
import type OrgMember from '@/types/OrgMember'

const props = defineProps<{ orgId: number }>()

const store = useStore()

const bridgeCustomers = ref<BridgeCustomer[]>([])
const bridgeLoading = ref(false)
const enrolling = ref<Set<number>>(new Set())
const expandedRows = ref<Set<number>>(new Set())
const copiedMap = ref<Map<number, 'tos' | 'kyb' | null>>(new Map())

const orgMembers = computed<OrgMember[]>(() => store.getters['org/getCurrentMembers'] ?? [])
const orgName = computed(() => store.getters['org/getSelectedOrg']?.organization_name ?? '')
const membersLoading = computed(() => !orgMembers.value.length)

const bridgeByMemberId = computed(() => {
  const map = new Map<number, BridgeCustomer>()
  for (const c of bridgeCustomers.value) {
    map.set(c.member_id, c)
  }
  return map
})

const statusRank = (bridge: BridgeCustomer | null): number => {
  const s = kycStatus(bridge)
  if (s === 'approved') return 0
  if (s === 'in_progress') return 1
  return 2
}

const rows = computed(() => {
  return orgMembers.value
    .map(m => ({ member: m, bridge: bridgeByMemberId.value.get(m.member_id) ?? null }))
    .sort((a, b) => {
      const rankDiff = statusRank(a.bridge) - statusRank(b.bridge)
      if (rankDiff !== 0) return rankDiff
      return (a.member.display_name || '').localeCompare(b.member.display_name || '')
    })
})

const kycStatus = (customer: BridgeCustomer | null): 'approved' | 'rejected' | 'in_progress' | 'not_enrolled' => {
  if (!customer) return 'not_enrolled'
  const kyc = customer.kyc_status
  if (kyc === 'approved') return 'approved'
  if (['rejected', 'paused', 'offboarded'].includes(kyc)) return 'rejected'
  return 'in_progress'
}

const isExpandable = (bridge: BridgeCustomer | null) =>
  bridge !== null && kycStatus(bridge) === 'in_progress'

const toggleRow = (memberId: number) => {
  if (expandedRows.value.has(memberId)) {
    expandedRows.value.delete(memberId)
  } else {
    expandedRows.value.add(memberId)
  }
}

const tosUrl = (bridge: BridgeCustomer | null) => bridge?.link?.tos_url ?? null
const kycUrl = (bridge: BridgeCustomer | null) => bridge?.link?.kyc_url ?? null

const copyLink = async (memberId: number, url: string, which: 'tos' | 'kyb') => {
  await navigator.clipboard.writeText(url)
  copiedMap.value.set(memberId, which)
  setTimeout(() => copiedMap.value.set(memberId, null), 2000)
}

const fetchBridgeCustomers = async () => {
  bridgeLoading.value = true
  try {
    const response = await API({ method: 'GET', url: `${HOST}/payments/bridge/customers` })
    bridgeCustomers.value = response.data
  } finally {
    bridgeLoading.value = false
  }
}

const enroll = async (member: OrgMember) => {
  enrolling.value.add(member.member_id)
  try {
    // TODO: reconnect when ready for production
    // await API({
    //   method: 'POST',
    //   url: `${HOST}/payments/bridge/customers`,
    //   data: { member_id: member.member_id }
    // })
    // await fetchBridgeCustomers()

    // Mock: simulate enrollment with a short delay
    await new Promise(resolve => setTimeout(resolve, 800))
    bridgeCustomers.value.push({
      id: -member.member_id,
      member_id: member.member_id,
      bridge_customer_id: null,
      kyc_status: 'not_started',
      tos_status: 'pending',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      link: null
    } as any)
  } finally {
    enrolling.value.delete(member.member_id)
  }
}

const getInitials = (m: OrgMember) => {
  if (m.first_name && m.last_name) return `${m.first_name[0]}${m.last_name[0]}`.toUpperCase()
  return (m.display_name || m.email || '?').slice(0, 2).toUpperCase()
}

onMounted(async () => {
  store.dispatch('org/fetchOrgMembers', props.orgId)
  fetchBridgeCustomers()
})
</script>

<template>
  <div class="space-y-3">
    <div class="flex items-center justify-between">
      <h3 class="text-sm font-semibold">{{ orgName ? `${orgName} Members` : 'Members' }}</h3>
      <span v-if="!membersLoading" class="text-xs text-muted-foreground">{{ rows.length }} total</span>
    </div>

    <!-- Loading -->
    <div v-if="membersLoading || bridgeLoading" class="space-y-2">
      <div v-for="i in 4" :key="i" class="h-12 bg-muted rounded animate-pulse" />
    </div>

    <!-- Table -->
    <div v-else class="border rounded-lg overflow-x-auto">
      <table class="w-full text-sm">
        <thead class="border-b bg-muted/50">
          <tr>
            <th class="w-8"></th>
            <th class="px-4 py-3 text-left font-medium text-muted-foreground">Member</th>
            <th class="px-4 py-3 text-center font-medium text-muted-foreground">KYC Status</th>
            <th class="px-4 py-3 text-left font-medium text-muted-foreground">Org Role</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="{ member, bridge } in rows" :key="member.member_id">

            <!-- Main row -->
            <tr
              class="border-b hover:bg-muted/30 transition-colors"
              :class="[expandedRows.has(member.member_id) ? 'bg-muted/20' : '', isExpandable(bridge) ? 'cursor-pointer' : '']"
              @click="isExpandable(bridge) && toggleRow(member.member_id)"
            >
              <!-- Expand toggle -->
              <td class="pl-3 py-3 text-center">
                <button
                  v-if="isExpandable(bridge)"
                  @click="toggleRow(member.member_id)"
                  class="text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                >
                  <ChevronDown v-if="expandedRows.has(member.member_id)" class="h-4 w-4" />
                  <ChevronRight v-else class="h-4 w-4" />
                </button>
              </td>

              <!-- Member -->
              <td class="px-4 py-3">
                <div class="flex items-center gap-2">
                  <Avatar class="h-7 w-7 flex-shrink-0">
                    <AvatarImage v-if="member.avatar_url" :src="member.avatar_url" />
                    <AvatarFallback class="text-xs">{{ getInitials(member) }}</AvatarFallback>
                  </Avatar>
                  <span class="font-medium truncate">{{ member.display_name || 'Unnamed' }}</span>
                </div>
              </td>

              <!-- KYC Status -->
              <td class="px-4 py-3 text-center">
                <Badge
                  v-if="kycStatus(bridge) === 'in_progress'"
                  class="bg-amber-100 text-amber-700 border-amber-200"
                >
                  <Clock class="h-3 w-3 mr-1" />
                  {{ bridge?.kyc_status }}
                </Badge>
                <Badge
                  v-else-if="kycStatus(bridge) === 'approved'"
                  class="bg-green-100 text-green-700 border-green-200 hover:bg-green-100"
                >
                  <CheckCircle2 class="h-3 w-3 mr-1" />
                  Verified
                </Badge>
                <Badge
                  v-else-if="kycStatus(bridge) === 'rejected'"
                  class="bg-red-100 text-red-700 border-red-200 hover:bg-red-100"
                >
                  <XCircle class="h-3 w-3 mr-1" />
                  Rejected
                </Badge>
                <Button
                  v-else
                  size="sm"
                  variant="outline"
                  :disabled="enrolling.has(member.member_id)"
                  @click="enroll(member)"
                >
                  <Loader2 v-if="enrolling.has(member.member_id)" class="h-3 w-3 mr-1 animate-spin" />
                  <UserPlus v-else class="h-3 w-3 mr-1" />
                  Enroll
                </Button>
              </td>

              <!-- Org Role -->
              <td class="px-4 py-3">
                <span class="text-xs text-muted-foreground">{{ member.role.replace(/^org_/i, '').replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) }}</span>
              </td>
            </tr>

            <!-- Expanded row -->
            <tr v-if="expandedRows.has(member.member_id)" class="border-b bg-slate-50 dark:bg-slate-900">
              <td colspan="4" class="px-6 py-4">
                <div class="flex flex-col gap-3">

                  <!-- ToS link card -->
                  <div class="w-full max-w-md rounded-lg border bg-white dark:bg-slate-800 p-3 space-y-2">
                    <div class="flex items-center gap-2">
                      <FileText class="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span class="text-xs font-medium">Terms of Service</span>
                    </div>
                    <div v-if="tosUrl(bridge)" class="flex items-center gap-2 overflow-hidden">
                      <a
                        :href="tosUrl(bridge)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="min-w-0 text-xs font-mono text-blue-600 hover:underline truncate"
                      >{{ tosUrl(bridge) }}</a>
                      <button
                        @click="copyLink(member.member_id, tosUrl(bridge)!, 'tos')"
                        class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                        title="Copy"
                      >
                        <Check v-if="copiedMap.get(member.member_id) === 'tos'" class="h-3.5 w-3.5 text-green-600" />
                        <Copy v-else class="h-3.5 w-3.5" />
                      </button>
                      <a
                        :href="tosUrl(bridge)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ExternalLink class="h-3.5 w-3.5" />
                      </a>
                    </div>
                    <p v-else class="text-xs text-muted-foreground">Link unavailable</p>
                  </div>

                  <!-- KYB link card -->
                  <div class="w-full max-w-md rounded-lg border bg-white dark:bg-slate-800 p-3 space-y-2">
                    <div class="flex items-center gap-2">
                      <FileText class="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                      <span class="text-xs font-medium">Customer Verification (KYC)</span>
                    </div>
                    <div v-if="kycUrl(bridge)" class="flex items-center gap-2 overflow-hidden">
                      <a
                        :href="kycUrl(bridge)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="min-w-0 text-xs font-mono text-blue-600 hover:underline truncate"
                      >{{ kycUrl(bridge) }}</a>
                      <button
                        @click="copyLink(member.member_id, kycUrl(bridge)!, 'kyb')"
                        class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                        title="Copy"
                      >
                        <Check v-if="copiedMap.get(member.member_id) === 'kyb'" class="h-3.5 w-3.5 text-green-600" />
                        <Copy v-else class="h-3.5 w-3.5" />
                      </button>
                      <a
                        :href="kycUrl(bridge)!"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <ExternalLink class="h-3.5 w-3.5" />
                      </a>
                    </div>
                    <p v-else class="text-xs text-muted-foreground">Link unavailable</p>
                  </div>

                </div>
              </td>
            </tr>

          </template>
        </tbody>
      </table>
    </div>
  </div>
</template>
